import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import Grid from "@mui/material/Grid2";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import SearchIcon from "@mui/icons-material/Search";

import ApoderadosApi from "api/apoderadosApi";
import CreateApoderado from "./createApoderado";

function SelectApoderado({ open, onClose, onSelect, onSuccess = () => {} }) {
  const [apoderados, setApoderados] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const searchInputRef = useRef(null);

  const obtenerApoderados = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApoderadosApi.fetchApoderadosSoft();
      setApoderados(response.data);
      //const recurrentes = response.data.filter((apoderado) => apoderado.es_recurrente);
      //setApoderados(recurrentes);
    } catch (error) {
      console.error("Error al cargar apoderados:", error);
      setError(obtenerMensajeError(error, "Error al cargar los apoderados"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !showCreateForm) {
      obtenerApoderados();
      // Focus en el input de búsqueda cuando se abre el modal
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
    // Limpiar el término de búsqueda cuando se cierra el modal
    if (!open) {
      setSearchTerm("");
    }
  }, [open, showCreateForm]);

  const handleClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      if (document.activeElement) {
        document.activeElement.blur();
      }
    }
    setSearchTerm("");
    onClose();
  };

  const handleApoderadoSelect = (apoderado) => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
    onSelect(apoderado);
    setSearchTerm("");
    onClose();
  };

  const handleCreateApoderadoClick = () => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
    setShowCreateForm(true);
  };

  const handleCreateFormClose = () => {
    setShowCreateForm(false);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const filteredApoderados = apoderados.filter((apoderado) => {
    const searchLower = searchTerm?.toLowerCase();
    return (
      apoderado?.nombre_compleo?.toLowerCase().includes(searchLower) ||
      apoderado?.identificacion.toString().includes(searchLower)
    );
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={false}
      aria-labelledby="select-apoderado-title"
    >
      <DialogTitle id="select-apoderado-title">
        <SuiTypography variant="h6" component="div">
          {showCreateForm ? "Nuevo Apoderado" : "Buscar Apoderado"}
        </SuiTypography>
      </DialogTitle>

      <DialogContent>
        <SuiBox>
          {showCreateForm ? (
            <CreateApoderado
              open={showCreateForm}
              onClose={handleCreateFormClose}
              onSelect={handleApoderadoSelect}
              onSuccess={() => {
                obtenerApoderados();
                if (onSuccess) onSuccess();
              }}
            />
          ) : (
            <>
              <Grid container spacing={2} alignItems="center">
                <Grid item size={6}>
                  <SuiInput
                    inputRef={searchInputRef}
                    placeholder="Buscar por nombre o identificación"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    startAdornment={<SearchIcon sx={{ color: "text.secondary", mr: 1 }} />}
                    fullWidth
                    autoFocus
                  />
                </Grid>
                <Grid item size={6}>
                  <SuiButton
                    variant="outlined"
                    buttonColor="info"
                    fullWidth
                    onClick={handleCreateApoderadoClick}
                  >
                    Crear Apoderado
                  </SuiButton>
                </Grid>
              </Grid>

              {loading && (
                <SuiBox mt={2}>
                  <SuiTypography variant="button" fontWeight="regular">
                    Cargando Apoderados...
                  </SuiTypography>
                </SuiBox>
              )}
              {error && (
                <SuiBox mt={2}>
                  <SuiTypography variant="button" fontWeight="regular" color="error">
                    {error}
                  </SuiTypography>
                </SuiBox>
              )}
              <SuiBox mt={3}>
                <Grid container spacing={2}>
                  {filteredApoderados.map((apoderado) => (
                    <Grid item size={12} key={apoderado.id}>
                      <SuiButton
                        fullWidth
                        variant="outlined"
                        buttonColor="dark"
                        onClick={() => handleApoderadoSelect(apoderado)}
                      >
                        {apoderado.nombre_completo} - {apoderado.identificacion}
                      </SuiButton>
                    </Grid>
                  ))}
                  {!loading && filteredApoderados.length === 0 && (
                    <Grid item xs={12}>
                      <SuiTypography
                        variant="button"
                        fontWeight="regular"
                        textAlign="center"
                        component="div"
                      >
                        No se encontraron apoderados
                      </SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </SuiBox>
            </>
          )}
        </SuiBox>
      </DialogContent>

      {!showCreateForm && (
        <DialogActions>
          <SuiButton onClick={handleClose} buttonColor="light">
            Cancelar
          </SuiButton>
        </DialogActions>
      )}
    </Dialog>
  );
}

SelectApoderado.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default SelectApoderado;
